import type { DatePreset } from '../leads/date-range-select';

/** The two Analytics views: per-campaign results (the default — campaigns are the main feature), or the Bio Page report. */
export const ANALYTICS_VIEWS = ['bio', 'campaigns'] as const;
export type AnalyticsView = (typeof ANALYTICS_VIEWS)[number];
export const ANALYTICS_VIEW_LABELS: Record<AnalyticsView, string> = { bio: 'Bio Page', campaigns: 'Campaigns' };

export interface AnalyticsSearch {
  view?: AnalyticsView;
  campaign?: string;
  range?: DatePreset;
}

/**
 * Default range per view: the Bio Page report compares against the previous period, so it opens on the
 * last 30 days; campaign analytics keeps its lifetime view.
 */
export function defaultAnalyticsRange(view: AnalyticsView): DatePreset {
  return view === 'bio' ? '30d' : 'all';
}

/**
 * Effective view + range from the URL. An explicit `?view=` wins; a `?campaign=` deep link opens Campaigns;
 * otherwise the view this browser used last, else Campaigns.
 */
export function resolveAnalyticsSearch(
  search: AnalyticsSearch,
  lastView?: AnalyticsView
): { view: AnalyticsView; range: DatePreset } {
  const view = search.view ?? (search.campaign ? 'campaigns' : (lastView ?? 'campaigns'));
  return { view, range: search.range ?? defaultAnalyticsRange(view) };
}

const LAST_VIEW_STORAGE_KEY = 'hayven:marketing-analytics-view';

/** The Analytics view this browser used last; undefined when none was stored or storage is unavailable. */
export function readLastAnalyticsView(): AnalyticsView | undefined {
  try {
    const stored = window.localStorage.getItem(LAST_VIEW_STORAGE_KEY);
    return (ANALYTICS_VIEWS as readonly string[]).includes(stored ?? '') ? (stored as AnalyticsView) : undefined;
  } catch {
    return undefined; // Private mode / blocked storage: fall back to the default view.
  }
}

/** Remember the view for the next visit (a per-browser convenience; failures are ignored). */
export function rememberAnalyticsView(view: AnalyticsView): void {
  try {
    window.localStorage.setItem(LAST_VIEW_STORAGE_KEY, view);
  } catch {
    // Storage unavailable — the next visit simply opens the default view.
  }
}

/** URL search after switching view: the view becomes explicit and a range the user picked carries over. */
export function nextSearchForView(previous: AnalyticsSearch, view: AnalyticsView): AnalyticsSearch {
  return { ...previous, view };
}

/**
 * URL search after picking a range. The pick is always written explicitly (never stripped as "the default"),
 * so it survives a view switch even when it equals one view's default.
 */
export function nextSearchForRange(previous: AnalyticsSearch, range: DatePreset): AnalyticsSearch {
  return { ...previous, range };
}
